/* eslint-disable @typescript-eslint/no-explicit-any */
import FJMathUtil from '@src/util/FJMathUtil';
import FJCutVideoSlice from '@src/redux/slice/modal/FJCutVideoSlice.ts';
import FJTrimVideoManager from '@src/manager/FJTrimVideoManager.ts';
import type { AppThunk } from '@src/redux/appThunk.ts';
import { FJModalsAction } from '@src/redux/action/FJModalsAction.ts';
import FJCutConfig from '@src/config/FJCutConfig.ts';
import FJCommonAPI from '@src/core/network/FJCommonAPI.ts';
import FJProjectStore from '@src/store/FJProjectStore';
import FJLocalStore from '@src/store/FJLocalStore';
import FJMessageStore from '@src/store/FJMessageStore';
import { FJDetailsPageAction, type FJDetailsSubtitleItem } from '@src/redux/action/FJDetailsPageAction.ts';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';

let debounceUpdateThumbnailUrl: Function | null = null;
let _timeLineContainerWidth: number = 100;
let _unitConversion: number = 1;
let _originalTimeData: { startTime: number; endTime: number } = { startTime: 0, endTime: 0 };

const FJTrimAction = {
    initAction:
        (id: string, row: any): AppThunk =>
        async (dispatch, getState) => {
            FJTrimVideoManager.clear();

            if (!navigator.onLine) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                return;
            }

            FJTrimVideoManager.id = id;
            const video = FJTrimVideoManager.getVideo();
            if (!video) {
                return;
            }

            const pid = FJProjectStore.getEventId();
            FJRequirementsUtil.eaLog('short_card_trim_click', {
                project_id: pid,
                short_id: id,
            });

            if (getState().modal.cut.thumbnailBlobUrl) {
                URL.revokeObjectURL(getState().modal.cut.thumbnailBlobUrl);
            }

            let isCancel = false;
            dispatch(
                FJModalsAction.showLoadingModalAction(FJLocalStore._('loading'), () => {
                    isCancel = true;
                }),
            );
            const eventId = FJProjectStore.getEventId() as string;
            const data = await FJCommonAPI.getAiExtensionInfo(eventId).catch(() => {});
            dispatch(FJModalsAction.hideLoadingModalAction());
            if (isCancel) {
                return;
            }
            if (!data || data instanceof Array) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                return;
            }
            const subtitleList = data.data.map(item => {
                return {
                    text: item.text,
                    start: item.start_second,
                    end: item.end_second,
                    isTranslated: item.is_translated,
                    transcriptionText: item.transcription_text,
                    transcriptionWords: item.transcription_words.map(i => ({
                        text: i.text,
                        start: i.start_second,
                        end: i.end_second,
                    })),
                };
            });

            dispatch(FJCutVideoSlice.actions.setSubtitleList(subtitleList));
            const url = video.getSourceUrl();
            const info: any = video.getSourceInfo();
            const { duration, width, height } = info;
            FJTrimVideoManager.duration = duration;
            FJTrimVideoManager.setSource(url);
            // 更新数据
            dispatch(FJCutVideoSlice.actions.setTitle(row.title));
            // 更新时间点
            dispatch(FJCutVideoSlice.actions.setSliceTotalTime(duration));
            dispatch(FJTrimAction.setStartTimeAction(row.start));
            dispatch(FJTrimAction.setEndTimeAction(row.end));
            dispatch(FJTrimAction.seekAction(row.start));
            _originalTimeData = { startTime: row.start, endTime: row.end };

            // 更新视频来源
            dispatch(FJCutVideoSlice.actions.setThumbnailImageUrl(row.thumbnailUrl)); // 背景模糊的缩略图
            dispatch(FJCutVideoSlice.actions.setThumbnailBlobUrl('')); // 视频解析出的缩略图
            // dispatch(FJCutVideoSlice.actions.setSourceUrl(url));

            dispatch(FJCutVideoSlice.actions.setRatio(width / height));

            dispatch(FJTrimAction.updateUnitConversionAction(_timeLineContainerWidth, true));
            dispatch(FJTrimAction.updateThumbnailAction());

            dispatch(FJModalsAction.showTrimModalAction());
        },
    updateUnitConversionAction:
        (newContainerWidth: number, isResetUnitConversion = true): AppThunk =>
        (dispatch, getState) => {
            void getState();
            const totalTime = FJTrimVideoManager.duration;

            const oldContainerWidth = _timeLineContainerWidth;
            _timeLineContainerWidth = newContainerWidth;
            if (!FJTrimVideoManager.id) {
                return;
            }

            const minWidth = newContainerWidth * FJCutConfig.minWidthScale;
            const maxWidth = newContainerWidth * FJCutConfig.maxWidthScale;

            const minUnitConversion = minWidth / totalTime;
            const maxUnitConversion = maxWidth / totalTime;

            let newUnitConversion;
            if (isResetUnitConversion) {
                newUnitConversion = FJMathUtil.getValueInRange(
                    newContainerWidth / totalTime,
                    minUnitConversion,
                    maxUnitConversion,
                );
            } else {
                newUnitConversion = _unitConversion * (newContainerWidth / oldContainerWidth);
            }
            _unitConversion = newUnitConversion;
            dispatch(FJCutVideoSlice.actions.setSliceUnitConversion(newUnitConversion));
            dispatch(FJCutVideoSlice.actions.setSliceMinUnitConversion(minUnitConversion));
            dispatch(FJCutVideoSlice.actions.setSliceMaxUnitConversion(maxUnitConversion));
        },
    setUnitConversionAction:
        (value: number): AppThunk =>
        (dispatch, getState) => {
            void getState();
            _unitConversion = value;
            dispatch(FJCutVideoSlice.actions.setSliceUnitConversion(value));
            setTimeout(() => {
                dispatch(FJTrimAction.updateThumbnailAction());
            }, 100);
        },
    fitUnitConversionAction: (): AppThunk => (dispatch, getState) => {
        const totalTime = getState().modal.cut.totalTime;
        const value = _timeLineContainerWidth / totalTime;
        dispatch(FJTrimAction.setUnitConversionAction(value));
    },
    updateThumbnailAction: (): AppThunk => (dispatch, getState) => {
        // const thumbnailBlobUrl = getState().modal.cut.thumbnailBlobUrl;
        //
        // const totalTime = FJTrimVideoManager.duration;
        //
        // const thumbnailSize = {
        //     width: _unitConversion * totalTime,
        //     height: 60,
        // };
        //
        // if (!thumbnailSize.width || isNaN(thumbnailSize.width)) {
        //     return;
        // }
        //
        // if (!debounceUpdateThumbnailUrl) {
        //     debounceUpdateThumbnailUrl = debounce(
        //         async (thumbnailSize, thumbnailBlobUrl) => {
        //             const v = FJTrimVideoManager.getVideo();
        //             if (!v) {
        //                 return;
        //             }
        //             // if (!thumbnailBlobUrl) {
        //             //     const firstThumbnailBlobUrl = await v.getThumbnailBlobUrlByFirstFrame(thumbnailSize);
        //             //     dispatch(FJCutVideoSlice.actions.setThumbnailBlobUrl(firstThumbnailBlobUrl));
        //             // }
        //
        //             const newThumbnailBlobUrl = await v.updateThumbnailBlobUrl(thumbnailSize).catch(e => {
        //                 console.log(e);
        //             });
        //
        //             if (thumbnailBlobUrl) {
        //                 URL.revokeObjectURL(thumbnailBlobUrl);
        //             }
        //             dispatch(FJCutVideoSlice.actions.setThumbnailBlobUrl(newThumbnailBlobUrl));
        //         },
        //         200,
        //         { trailing: true },
        //     );
        // }
        // debounceUpdateThumbnailUrl(thumbnailSize, thumbnailBlobUrl);
    },
    /** 定位时间；播放中仅 seek，不改变播放态（与详情页预览进度条一致） */
    seekAction:
        (time: number): AppThunk =>
        dispatch => {
            // dispatch(FJTrimAction.pauseAction());
            FJTrimVideoManager.seek(time);
            dispatch(FJCutVideoSlice.actions.setSliceCurrentTime(time));
        },
    playAction: (): AppThunk => dispatch => {
        dispatch(FJCutVideoSlice.actions.setIsPlaying(true));
        FJTrimVideoManager.play(
            time => {
                dispatch(FJCutVideoSlice.actions.setSliceCurrentTime(time));
            },
            () => {
                dispatch(FJCutVideoSlice.actions.setIsPlaying(false));
            },
            () => {
                dispatch(FJCutVideoSlice.actions.setIsPlaying(false));
                dispatch(FJTrimAction.seekAction(FJTrimVideoManager.startTime));
            },
        ).catch(() => {
            dispatch(FJTrimAction.pauseAction());
        });
        dispatch(FJCutVideoSlice.actions.setIsPlaying(true));
    },
    pauseAction: (): AppThunk => dispatch => {
        FJTrimVideoManager.pause();
        dispatch(FJCutVideoSlice.actions.setIsPlaying(false));
    },
    setStartTimeAction:
        (time: number): AppThunk =>
        dispatch => {
            dispatch(FJTrimAction.pauseAction());
            FJTrimVideoManager.startTime = time;
            dispatch(FJCutVideoSlice.actions.setStartTime(time));
        },
    setEndTimeAction:
        (time: number): AppThunk =>
        dispatch => {
            dispatch(FJTrimAction.pauseAction());
            FJTrimVideoManager.endTime = time;
            dispatch(FJCutVideoSlice.actions.setEndTime(time));
        },
    setFilteredSubtitleListAction:
        (list: any[]): AppThunk =>
        () => {
            FJTrimVideoManager.trimSubtitleList = list;
        },
    saveAction: (): AppThunk => dispatch => {
        dispatch(FJModalsAction.hideTrimModalAction());
        FJTrimVideoManager.pause();
        const pid = FJProjectStore.getEventId();
        const noChanged =
            FJMathUtil.isValueEqual(_originalTimeData.startTime, FJTrimVideoManager.startTime, 0.1) &&
            FJMathUtil.isValueEqual(_originalTimeData.endTime, FJTrimVideoManager.endTime, 0.1);

        FJRequirementsUtil.eaLog('short_card_trim_save_click', {
            project_id: pid,
            short_id: FJTrimVideoManager.id,
            has_edited_segment: !noChanged,
            select_duration: !noChanged
                ? ((FJTrimVideoManager.endTime - FJTrimVideoManager.startTime) / 60).toFixed(2)
                : '/',
        });

        if (noChanged) {
            return;
        }

        const trimDataList: FJDetailsSubtitleItem[] = FJTrimVideoManager.trimSubtitleList;
        const applyList = trimDataList.map((item: FJDetailsSubtitleItem) => {
            return {
                ...item,
                transcriptionText: '',
                transcriptionWords: item.transcriptionWords.filter(word => {
                    return word.start >= item.start && word.end <= item.end;
                }),
            };
        });

        dispatch(
            FJDetailsPageAction.applyTrimClipAction(FJTrimVideoManager.id, {
                startTime: FJTrimVideoManager.startTime,
                endTime: FJTrimVideoManager.endTime,
                subTitleList: applyList,
            }),
        );
    },
    cancelAction:
        (fromClose: boolean = false): AppThunk =>
        dispatch => {
            const noChanged =
                FJMathUtil.isValueEqual(_originalTimeData.startTime, FJTrimVideoManager.startTime, 0.1) &&
                FJMathUtil.isValueEqual(_originalTimeData.endTime, FJTrimVideoManager.endTime, 0.1);

            FJRequirementsUtil.eaLog('short_card_trim_cancel_and_close_click', {
                project_id: FJProjectStore.getEventId(),
                short_id: FJTrimVideoManager.id,
                has_edited_segment: !noChanged,
                source: fromClose ? 'close' : 'cancel',
            });

            dispatch(FJModalsAction.hideTrimModalAction());
        },
    updateVideoCurrentTimeWhenZoneChangedAction: (): AppThunk => dispatch => {
        dispatch(FJTrimAction.seekAction(FJTrimVideoManager.startTime));
    },
};

export default FJTrimAction;
